import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { DialogService } from 'primeng/dynamicdialog';

import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { datetimeFormat } from 'app/constants/date-time-format';
import { CalculationService, DataService, HelperService, HTTPService, FormatService, DateService } from 'app/services';
import { currency } from 'app/constants/currency.constants';

import { find, findIndex } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-deferred-services',
  templateUrl: './deferred-services.component.html',
  styleUrls: ['./deferred-services.component.scss'],
})
export class DeferredServicesComponent implements OnInit, OnDestroy {
  @Input() orderStatus: string = '';
  @Input() selectedInternalServices: any = {};
  @Input() selectedServiceInspection: any = {};
  @Input() deferredServices: any[] = [];

  usd: string = currency.USD;
  dataListener: Subscription;

  visibleDeferredServices: any[] = [];
  isSubletTrackingEnabled: boolean = false;

  constructor(
    private calculationService: CalculationService,
    private httpService: HTTPService,
    private dataService: DataService,
    private helperService: HelperService,
    private dialogService: DialogService,
    private formatService: FormatService,
    private dateService: DateService,
    private currencyPipe: CurrencyPipe
  ) {}

  ngOnInit() {
    this.setDataService();
    this.deferredServices = this.formatServices(this.deferredServices);
    this.visibleDeferredServices = this.helperService.deepCopy(this.deferredServices);
  }

  ngOnDestroy() {
    if (this.dataListener) {
      this.dataListener.unsubscribe();
    }
  }

  setDataService() {
    this.dataListener = this.dataService.getData().subscribe(({ data }) => {
      if (data.key === 'motor-on-search') {
        this.visibleDeferredServices = this.deferredServices.filter(({ title }) => new RegExp(data.data.query, 'i').test(title));
      }
    });
    this.isSubletTrackingEnabled = this.dataService.isSubletTrackingEnabled;
  }

  setServiceTotals(service: any) {
    const data = {
      hours: 0,
      parts: 0,
      tires: 0,
      fees: 0,
      other: 0,
      sublet:0,
      partCost: 0,
      laborCost: 0,
      tireCost: 0,
      feeCost: 0,
      otherCost: 0,
      subletCost: 0,
      isAdded: false,
    };

    if (service.items && service.items.length) {
      service.items.forEach((item: any) => {
        if (item.type === 'labor') {
          data.hours += 1 * item.count;
          data.laborCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'part') {
          data.parts += 1 * item.count;
          data.partCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'tire') {
          data.tires += 1 * item.count;
          data.tireCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'hazmatFee') {
          data.fees += 1 * item.count;
          data.feeCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'other') {
          data.other += 1 * item.count;
          data.otherCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'sublet') {
          data.sublet += 1 * item.count;
          data.subletCost += this.calculationService.roundOff(item.subTotal);
        }
      });
    }

    return data;
  }

  setInspectionCounts(inspection: any = {}) {
    let categoryCount = 0;
    let itemCount = 0;
    let isItemExists = false;

    if (inspection.inspectionId && inspection.inspectionId.category && inspection.inspectionId.category.length) {
      inspection.inspectionId.category.forEach((category: any) => {
        if (category.inspectionItems.length) {
          category.inspectionItems.forEach(({ isChecked }: any) => {
            if (isChecked) {
              itemCount += 1;
              isItemExists = true;
            }
          });
        }

        if (isItemExists) {
          categoryCount += 1;
          isItemExists = false;
        }
      });
    }

    return {
      categoryCount,
      itemCount,
    };
  }

  /** Add the service and all the items to inspection */
  addToEstmate(service: any, handleSelectAll: Function, event: any) {
    event.stopPropagation();

    const { _id } = service,
      isAdded = this.selectedInternalServices[_id];

    if (!isAdded) {
      this.selectedInternalServices[_id] = {
        ...service,
        extra: { isAdded: true },
      };
    } else {
      delete this.selectedInternalServices[_id];
    }

    handleSelectAll(!isAdded ? 'select' : 'deselect', event, true, true);
  }

  handleSelectChange(event: any) {
    const { list, id } = event;
    const service = find(this.deferredServices, { _id: id });

    if (service) {
      if (list.length) {
        this.selectedInternalServices[id] = {
          ...service,
          extra: { isAdded: true },
          items: this.helperService.deepCopy(list),
        };
      } else {
        delete this.selectedInternalServices[id];
      }
    }
  }

  handleInspectionSelectChange(event: any, service: any) {
    this.selectedServiceInspection[service._id] = {
      _id: service._id,
      isInspectionChecked: true,
      items: service.items,
    };
  }

  handleROLinkClick(estimateId: string) {
    this.helperService.openPageInNewExistingTab(`/estimate/${estimateId}`, estimateId);
  }

  handleDeleteDeferredService(event: Event, serviceId: string) {
    event.preventDefault();
    event.stopPropagation();

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: 'Remove the service from deferred services list?',
        message: 'This service will be marked as declined and will be permanently removed from deferred services list.',
        button: {
          success: { title: 'Confirm' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.httpService.deleteCustomerDeferredService(serviceId).then(
          (res) => {
            let sIndex = findIndex(this.deferredServices);
            let dIndex = findIndex(this.visibleDeferredServices);

            if (sIndex !== -1) {
              this.deferredServices.splice(sIndex, 1);
            }

            if (dIndex !== -1) {
              this.visibleDeferredServices.splice(dIndex, 1);
            }

            this.helperService.showToaster('success', 'Successfully deleted');
          },
          (err) => {
            this.helperService.showToaster('error', err);
          }
        );
      }
      onClose.unsubscribe();
    });
  }

  formatServices(services: any[] = []) {
    if (services && services.length) {
      services.forEach((service: any) => {
        if (service.serviceAuthHistory && service.serviceAuthHistory.length) {
          service.serviceAuthHistory = this.formatService.formatServiceHistory(service.serviceAuthHistory);
          service.tooltipDeferred = this.showlist(service);
        }
      });
    }
    return services;
  }

  showlist(service: any) {
    let allServices = ``;
    service.serviceAuthHistory.forEach((history: any) => {
      allServices += `
      <div class="tooltip-2">
        <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
          <div class="tech-service font-11 font-gray-35 d-flex">
            <i class="fal fa-history align-self-start" style="padding-top: 2px;"></i>
            <span class="tech-service-title">
            ${this.dateService.formatDate(history.createdAt, datetimeFormat.mmmdyyhhmmaa)}
            </span>
          </div>
          <div class="tech-status px-2 font-11">
            <span class="truncate d-inline-block" style="max-width: 115px">
              ${history.methodText || ''}
            </span>
          </div>
          <div class="tech-name pr-2">
            <span>
              by ${history.fullName}
            </span>
          </div>
          <div class="tech-amount">
            <span>
            ${this.currencyPipe.transform(history.serviceTotal || 0, this.usd)}
            </span>
          </div>
        </div>
      </div>`;
    });
    return `<div class="tooltip-seondary">
      <div class="tooltip-secondary-row font-14 font-secondary-800 divider-bottom">
            <i class="fal fa-clock"></i><strong>Deferred service: </strong>
            <span class="truncate d-inline-block" style="max-width: calc(100% - 150px); position: relative; top: 5px;">
            ${service.title}</span>
      </div>
      ${allServices}
      </div>`;
  }
}
